import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff, Minus, Package, Plus, Search, Send, ShoppingCart, Tag, Trash2 } from 'lucide-react';
import {
  api,
  STATUS_LABEL,
  type CatalogItem,
  type CatalogSearch,
  type Health,
  type QuoteCosts,
  type QuoteLine,
  type QuotePayload,
} from '@/lib/api';
import { cn, formatMoney, formatPct } from '@/lib/utils';
import { parseMoney, type PriceChange } from '@/lib/cart';
import { useCartEdits, type CartEdits } from '@/lib/useCartEdits';
import OrderNote from '@/components/OrderNote';

/**
 * Un bouton cliqué ne garde pas le focus : le lecteur de codes-barres tape dans le champ
 * de recherche, et sa touche Entrée ne doit jamais redéclencher un bouton (+, −, ajout…).
 */
const KEEP_FOCUS = { onMouseDown: (e: React.MouseEvent) => e.preventDefault() };

/** Durée d'affichage des coûtants avant qu'ils se masquent d'eux-mêmes. */
const REVEAL_MS = 15_000;

/** Valeur retardée, et `flush` pour la prendre tout de suite (Entrée du lecteur). */
function useDebounced<T>(value: T, ms = 300): [T, () => void] {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return [v, () => setV(value)];
}

/**
 * Coûtants cachés par défaut. Un toucher sur l'œil les montre pendant 15 s, puis
 * ils se masquent : le client qui regarde l'écran ne les voit pas par accident.
 * Les coûtants ne sont demandés au serveur qu'à ce moment-là.
 */
function useReveal() {
  const [revealed, setRevealed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const toggle = () => {
    clearTimeout(timer.current);
    if (revealed) return setRevealed(false);
    setRevealed(true);
    timer.current = setTimeout(() => setRevealed(false), REVEAL_MS);
  };
  return { revealed, toggle };
}

export default function QuotePage() {
  const { id } = useParams();
  const quoteId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { revealed, toggle } = useReveal();

  const cart = useCartEdits(quoteId);
  const quoteKey = ['quote', quoteId];
  const quote = useQuery({
    queryKey: quoteKey,
    queryFn: () => cart.read(() => api<QuotePayload>(`/api/quotes/${quoteId}`)),
  });

  const costs = useQuery({
    queryKey: ['quote-costs', quoteId, quote.data?.quote.updatedAt],
    queryFn: () => api<QuoteCosts>(`/api/quotes/${quoteId}/costs`),
    enabled: revealed && !!quote.data,
    // Relus à chaque révélation ; l'ancienne réponse reste affichée pendant la
    // relecture qui suit un changement du panier.
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  // Le lecteur de codes-barres tape dans le champ qui a le focus : après un ajout ou un changement
  // de ligne, réussi ou non, on revient au champ de recherche, texte sélectionné, pour que le scan
  // suivant le remplace. Le champ n'existe que pour une commande modifiable.
  const searchRef = useRef<HTMLInputElement>(null);
  const refocusSearch = () => {
    searchRef.current?.focus();
    searchRef.current?.select();
  };

  // Note enregistrée : seul l'en-tête est repris. Les lignes de sa réponse peuvent être
  // plus anciennes que le panier affiché (changements en attente de confirmation).
  const setHeader = (data: QuotePayload) => {
    qc.setQueryData<QuotePayload>(quoteKey, (d) => (d ? { ...d, quote: data.quote } : data));
    void qc.invalidateQueries({ queryKey: ['quotes'] });
  };

  const remove = useMutation({
    mutationFn: () => api(`/api/quotes/${quoteId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      navigate('/');
    },
  });

  if (quote.isPending) return <p className="text-muted-foreground">Chargement…</p>;
  if (quote.isError) return <p className="text-destructive">{(quote.error as Error).message}</p>;

  const { quote: q, customer, lines, totals } = quote.data;
  const editable = q.status === 'DRAFT';
  const costByLine = new Map(costs.data?.lines.map((l) => [l.id, l]));

  return (
    <>
      <section className="panel flex flex-wrap items-center gap-x-6 gap-y-3 p-4 sm:p-5">
        <div className="min-w-0 flex-1 basis-full sm:basis-auto">
          <Link
            to="/"
            className="text-primary -ml-1 inline-flex h-8 items-center gap-1 rounded-md px-1 text-sm font-medium hover:underline"
          >
            <ArrowLeft className="size-4" />
            Accueil
          </Link>
          <h1 className="page-title mt-1">{customer.name}</h1>
          <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 text-sm tabular-nums">
            <span className="text-foreground font-bold">#{customer.custNum}</span>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">Commande {q.number}</span>
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-px text-xs font-medium">
              {STATUS_LABEL[q.status]}
            </span>
          </p>
        </div>
        <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-end">
          <button
            onClick={toggle}
            aria-pressed={revealed}
            title={revealed ? 'Masquer les coûtants' : 'Afficher les coûtants'}
            className={cn(
              'flex size-11 items-center justify-center rounded-full border transition-colors',
              revealed ? 'border-amber-500 bg-amber-100 text-amber-900' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {revealed ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
          </button>
          <div className="text-right">
            <div className="text-3xl leading-none font-bold tabular-nums">{formatMoney(totals.total)}</div>
            <div className="text-muted-foreground mt-1 text-xs">Total · taxes en sus</div>
          </div>
        </div>
      </section>

      {/* Produits à gauche, panier à droite (empilés sur petit écran). */}
      <div className={cn('grid grid-cols-1 items-start gap-4', editable && 'lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_24rem]')}>
        {editable && (
          <ProductSearch
            custNum={customer.custNum}
            revealed={revealed}
            onAdd={cart.add}
            error={cart.error}
            searchRef={searchRef}
            refocusSearch={refocusSearch}
          />
        )}

        {/* Sous l'en-tête collant (barre bleue + damier) : le panier reste à l'écran pendant la recherche. */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <section id="panier" className="panel scroll-mt-24 overflow-hidden">
            <h2 className="panel-title flex items-center gap-2 border-b px-4 py-3">
              <ShoppingCart className="text-primary size-4" />
              Panier
              {/* La clé relance le petit rebond à chaque article ajouté ou retiré. */}
              <span
                key={totals.itemCount}
                className="bg-primary text-primary-foreground animate-bump ml-auto rounded-full px-2 py-px text-xs tabular-nums"
              >
                {totals.itemCount}
              </span>
            </h2>
            {quote.data.pricesStale && (
              <div role="status" className="flex flex-wrap items-center justify-between gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900">
                <span>Prix non mis à jour — Epicor indisponible.</span>
                <button
                  {...KEEP_FOCUS}
                  onClick={() => void quote.refetch()}
                  disabled={quote.isFetching || cart.touched.size > 0}
                  className="h-8 rounded-md border border-amber-600 px-3 font-medium hover:bg-amber-100 disabled:opacity-50"
                >
                  Réessayer
                </button>
              </div>
            )}
            {cart.error && (
              <p role="alert" className="text-destructive border-b bg-red-50 px-4 py-2 text-sm">
                {cart.error}
              </p>
            )}
            {lines.length === 0 ? (
              <p className="text-muted-foreground px-4 py-6 text-center text-sm">
                Aucun article. Cherchez un produit pour l'ajouter.
              </p>
            ) : (
              <ul className="divide-y px-4">
                {lines.map((line) => (
                  <LineRow
                    key={line.id}
                    line={line}
                    editable={editable}
                    syncing={line.id < 0 || cart.touched.has(line.id)}
                    cost={revealed ? costByLine.get(line.id) : undefined}
                    costUnavailable={costs.data?.unavailable === true}
                    cart={cart}
                    onDone={refocusSearch}
                  />
                ))}
              </ul>
            )}

            <dl className="bg-muted/60 space-y-1 border-t px-4 py-3 text-sm">
              <div className="flex justify-between">
                <dt>Sous-total</dt>
                <dd className="tabular-nums">{formatMoney(totals.subtotal)}</dd>
              </div>
              {totals.cores !== '0.00' && (
                <div className="flex justify-between">
                  <dt>Consignes / écofrais</dt>
                  <dd className="tabular-nums">{formatMoney(totals.cores)}</dd>
                </div>
              )}
              <div className="flex items-baseline justify-between pt-1">
                <dt className="font-bold">Total <span className="text-muted-foreground text-xs font-normal">taxes en sus</span></dt>
                <dd className="text-xl font-bold tabular-nums">{formatMoney(totals.total)}</dd>
              </div>
              {revealed && costs.data && (
                <div className="flex justify-between rounded-lg bg-amber-50 px-2 py-1 text-amber-900">
                  <dt>Coûtant · marge</dt>
                  <dd className="tabular-nums">
                    {/* Un coûtant absent n'est pas 0 $ : inconnu, ou indisponible si l'API Pièce est en panne. */}
                    {!costs.data.totals.complete && costs.data.totals.cost === '0.00' ? (
                      costs.data.unavailable ? (
                        'Coûtant indisponible'
                      ) : (
                        'Coûtant inconnu'
                      )
                    ) : (
                      <>
                        {formatMoney(costs.data.totals.cost)} · {formatMoney(costs.data.totals.margin)}
                        {costs.data.totals.marginPct !== null && ` (${formatPct(costs.data.totals.marginPct)})`}
                        {!costs.data.totals.complete && ' · coûtant incomplet'}
                      </>
                    )}
                  </dd>
                </div>
              )}
              {revealed && costs.data?.unavailable && (
                <div className="flex justify-between gap-2 rounded-lg bg-amber-50 px-2 py-1 text-amber-900">
                  <dt>Coûtant indisponible — réessayez</dt>
                  <dd>
                    <button type="button" {...KEEP_FOCUS} onClick={() => costs.refetch()} className="font-semibold underline">
                      Réessayer
                    </button>
                  </dd>
                </div>
              )}
            </dl>

            {editable && (
              <div className="space-y-1.5 border-t p-4">
                {/* Branché quand l'accès Aconnex sera fourni : la commande partira dans Eagle. */}
                <button
                  disabled
                  className="bg-primary text-primary-foreground btn-caps flex h-12 w-full items-center justify-center gap-2 rounded-lg px-5 disabled:opacity-50"
                >
                  <Send className="size-4" />
                  Transmettre à Eagle
                </button>
                <p className="text-muted-foreground text-center text-xs">Connexion Eagle (Aconnex) à venir.</p>
              </div>
            )}
          </section>

          <OrderNote data={quote.data} onChange={setHeader} />

          {editable && (
            <button
              onClick={() => confirm('Supprimer ce brouillon ?') && remove.mutate()}
              className="text-destructive hover:bg-card h-11 w-full rounded-lg px-5 text-sm font-semibold transition-colors"
            >
              Supprimer le brouillon
            </button>
          )}
        </aside>
      </div>

      {/* Téléphone et tablette en portrait : le panier est sous les résultats, cette barre y mène. */}
      {editable && lines.length > 0 && (
        <>
          <div aria-hidden className="h-16 lg:hidden" />
          <div className="bg-card fixed inset-x-0 bottom-0 z-20 flex items-center gap-3 border-t px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgb(20_20_20/0.08)] lg:hidden">
            <ShoppingCart className="text-primary size-5 shrink-0" />
            <span
              key={totals.itemCount}
              className="bg-primary text-primary-foreground animate-bump rounded-full px-2 py-px text-xs font-bold tabular-nums"
            >
              {totals.itemCount}
            </span>
            <span className="min-w-0 flex-1 text-lg font-bold tabular-nums">{formatMoney(totals.total)}</span>
            <button
              onClick={() => document.getElementById('panier')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="bg-primary text-primary-foreground hover:bg-primary-dark btn-caps h-11 shrink-0 rounded-lg px-4 transition-colors"
            >
              Voir le panier
            </button>
          </div>
        </>
      )}
    </>
  );
}

// --- Recherche de produits ------------------------------------------------------

function ProductSearch({
  custNum,
  revealed,
  onAdd,
  error,
  searchRef,
  refocusSearch,
}: {
  custNum: string;
  revealed: boolean;
  onAdd: CartEdits['add'];
  /** Dernier changement du panier refusé par le serveur. */
  error: string | null;
  searchRef: React.RefObject<HTMLInputElement | null>;
  refocusSearch: () => void;
}) {
  const [q, setQ] = useState('');
  const [typed, searchNow] = useDebounced(q.trim());
  // Commande ouverte : le curseur est déjà dans la recherche, le premier scan part tout de suite.
  useEffect(() => searchRef.current?.focus(), [searchRef]);
  // Code-barres validé par Entrée (le lecteur l'envoie après les chiffres) : le champ se
  // vide AUSSITÔT, prêt pour le scan suivant, sans attendre la réponse ; la recherche
  // porte sur ce code et ses résultats restent affichés jusqu'à la prochaine frappe.
  const [scanned, setScanned] = useState<string | null>(null);
  const term = scanned ?? typed;
  const isBarcode = (v: string) => /^\d{12,14}$/.test(v);
  // Catalogue pas encore en ligne mais prix Epicor réels : recherche par numéro seulement.
  const health = useQuery({ queryKey: ['health'], queryFn: () => api<Health>('/api/health'), staleTime: 300_000 });
  const partNumberOnly = health.data?.catalogueMode === 'mock' && health.data.tomcat === 'configured';

  const search = useQuery({
    queryKey: ['catalogue', custNum, term],
    queryFn: () =>
      api<CatalogSearch>(
        `/api/catalogue/search?q=${encodeURIComponent(term)}&customer=${encodeURIComponent(custNum)}`,
      ),
    enabled: term.length >= 2,
    placeholderData: (prev) => prev,
  });

  const items = term.length >= 2 ? (search.data?.items ?? []) : [];

  // Lecteur réglé sans Entrée : le code est cherché après le délai de frappe, et le
  // champ se vide quand il a trouvé son produit.
  const found = !search.isPlaceholderData && items.length > 0;
  useEffect(() => {
    if (found && isBarcode(term) && q.trim() === term) {
      setScanned(term);
      setQ('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, term]);

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    // Valeur du champ lui-même : le lecteur envoie Entrée juste après le dernier chiffre.
    const v = e.currentTarget.value.trim();
    if (isBarcode(v)) {
      setScanned(v);
      setQ('');
    } else {
      searchNow();
    }
  }
  const priceable = items.filter((it) => it.epicor.mfgCode);
  const costKeys = priceable.map((it) => `${it.epicor.partNum}|${it.epicor.mfgCode}`).join(',');

  const costs = useQuery({
    queryKey: ['catalogue-costs', costKeys],
    queryFn: () =>
      api<{ costs: Record<string, string | null>; unavailable?: boolean }>(`/api/catalogue/costs?items=${encodeURIComponent(costKeys)}`),
    enabled: revealed && costKeys.length > 0,
    // Relus à chaque révélation : un coûtant changé dans Epicor se voit tout de suite.
    staleTime: 0,
  });

  // Coûtant de la rangée (relu dans Epicor à chaque révélation).
  function costOf(it: CatalogItem) {
    return revealed ? costs.data?.costs[`${it.epicor.partNum}|${it.epicor.mfgCode}`] : undefined;
  }

  function addItem(it: CatalogItem, qty: number) {
    const body: Record<string, unknown> = {
      partNum: it.epicor.partNum,
      mfgCode: it.epicor.mfgCode,
      mfgCode2: it.epicor.mfgCode2,
      title: it.title,
      brand: it.brand,
      qty,
    };
    let unitPrice = it.price?.unitPrice ?? null;
    if (!unitPrice) {
      const typed = prompt(`« ${it.title} » n'a pas de prix Epicor.\nPrix unitaire à proposer :`);
      unitPrice = typed ? parseMoney(typed) : null;
      if (!unitPrice) {
        if (typed) alert('Prix invalide.');
        refocusSearch();
        return;
      }
      body.unitPrice = unitPrice;
    }
    refocusSearch();
    onAdd(body, {
      partNum: it.epicor.partNum,
      mfgCode: it.epicor.mfgCode!,
      title: it.title,
      brand: it.brand,
      qty,
      unitPrice,
      listPrice: it.price?.listPrice ?? null,
      coreCost: it.price?.coreCost ?? '0.00',
      priced: !!it.price?.unitPrice,
    });
  }

  return (
    <section className="panel min-w-0 overflow-hidden">
      <div className="relative border-b p-3 sm:p-4">
        <Search className="text-muted-foreground absolute top-1/2 left-6 size-5 -translate-y-1/2 sm:left-7" />
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setScanned(null);
          }}
          onKeyDown={onSearchKey}
          placeholder={partNumberOnly ? 'Numéro de pièce (ex. CSP739, 3461)' : 'Produit, n° de pièce, marque, pneu…'}
          inputMode="search"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Chercher un produit"
          className="border-input bg-card focus:border-primary focus:ring-primary/15 h-12 w-full rounded-lg border pr-3 pl-10 text-base focus:ring-4 focus:outline-none"
        />
      </div>

      {search.isError && <p className="text-destructive px-4 pt-3 text-sm">{(search.error as Error).message}</p>}
      {search.data?.barcodeUnavailable && (
        <p className="text-destructive px-4 pt-3 text-sm">
          Recherche par code-barres indisponible — réessayez{' '}
          <button type="button" {...KEEP_FOCUS} onClick={() => search.refetch()} className="font-semibold underline">
            Réessayer
          </button>
        </p>
      )}
      {revealed && costs.data?.unavailable && (
        <p className="px-4 pt-3 text-sm text-amber-800">
          Coûtant indisponible — réessayez{' '}
          <button type="button" {...KEEP_FOCUS} onClick={() => costs.refetch()} className="font-semibold underline">
            Réessayer
          </button>
        </p>
      )}
      {error && <p className="text-destructive px-4 pt-3 text-sm">{error}</p>}
      {scanned && (
        <p className="text-muted-foreground px-4 pt-3 text-sm">
          Code-barres <span className="text-foreground font-medium tabular-nums">{scanned}</span>
          {search.isFetching ? ' — recherche…' : ' — prêt pour le scan suivant.'}
        </p>
      )}
      {term.length < 2 && (
        <p className="text-muted-foreground px-4 py-10 text-center text-sm">
          {partNumberOnly
            ? 'Tapez un numéro de pièce : le prix affiché est celui de ce client.'
            : 'Cherchez par nom, numéro de pièce, marque ou grandeur de pneu : le prix affiché est celui de ce client.'}
        </p>
      )}
      {term.length >= 2 && search.data && items.length === 0 && (
        <p className="text-muted-foreground px-4 py-10 text-center text-sm">Aucun produit trouvé pour « {term} ».</p>
      )}

      {items.length > 0 && (
        // Tableau façon catalogue de comptoir à partir de la tablette.
        <div className="relative hidden overflow-x-auto sm:block">
          <table className="w-full min-w-[38rem] text-sm">
            <thead>
              <tr className="border-primary text-muted-foreground font-heading border-b-2 text-left text-[0.6875rem] font-bold tracking-wider whitespace-nowrap uppercase">
                <th className="py-2 pr-2 pl-4">Ligne</th>
                <th className="px-2 py-2">N° article</th>
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2 text-center">Qté</th>
                <th className="px-2 py-2 text-right">Prix vente</th>
                <th className="text-price-ink px-2 py-2 text-right">Prix client</th>
                <th className="px-2 py-2 text-right">Consigne</th>
                <th className="px-2 py-2 text-right">Dispo</th>
                <th className="py-2 pr-4 pl-2">
                  <span className="sr-only">Ajouter</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <ResultRow
                  key={`${it.source}-${it.partNumber}-${it.epicor.mfgCode}`}
                  it={it}
                  cost={costOf(it)}
                  onAdd={addItem}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.length > 0 && (
        // Téléphone : une rangée empilée par article, le prix client toujours à l'écran.
        <ul className="divide-y sm:hidden">
          {items.map((it) => (
            <ResultItem
              key={`${it.source}-${it.partNumber}-${it.epicor.mfgCode}`}
              it={it}
              cost={costOf(it)}
              onAdd={addItem}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

type ResultProps = {
  it: CatalogItem;
  cost: string | null | undefined;
  onAdd: (it: CatalogItem, qty: number) => void;
};

/** Quantité saisie et ajout au panier, partagés par la rangée du tableau et celle du téléphone. */
function useResultAdd({ it, onAdd }: ResultProps) {
  const [qty, setQty] = useState('1');
  // L'ajout s'affiche tout de suite : plusieurs ajouts de suite ne s'attendent pas.
  const canAdd = !!it.epicor.mfgCode;
  function add() {
    if (!canAdd) return;
    // Le serveur refuse plus de 9999 unités.
    onAdd(it, Math.min(9999, Math.max(1, Math.trunc(Number(qty)) || 1)));
    setQty('1');
  }
  // Le lecteur tape un caractère à la fois : dès qu'une frappe ferait dépasser 4 chiffres, la case
  // revient à 1 et se verrouille (chiffres suivants ignorés) ; l'Entrée qui suit n'ajoute rien et
  // lève le verrou. Un code-barres tapé dans une quantité n'ajoute donc jamais rien.
  const locked = useRef(false);
  const qtyInput = {
    value: qty,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (locked.current) return;
      const digits = e.target.value.replace(/\D/g, '');
      if (digits.length <= 4) {
        setQty(digits);
      } else {
        locked.current = true;
        setQty('1');
      }
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (locked.current) locked.current = false;
      else add();
    },
    onBlur: () => {
      locked.current = false;
    },
  };
  // Le bouton ne garde pas le focus après un clic : Entrée ne le redéclenche pas.
  const addButton = {
    ...KEEP_FOCUS,
    onClick: add,
    disabled: !canAdd,
    title: it.epicor.mfgCode ? 'Ajouter au panier' : 'Article sans code de ligne Epicor',
    'aria-label': `Ajouter ${it.partNumber} au panier`,
  };
  return { qtyInput, addButton };
}

function ResultItem(props: ResultProps) {
  const { it, cost } = props;
  const { qtyInput, addButton } = useResultAdd(props);
  const p = it.price;

  return (
    <li className="flex gap-3 px-4 py-3">
      <ProductThumb src={it.image} />
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-sm leading-snug font-medium">{it.title}</div>
        <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 text-xs tabular-nums">
          <span className="text-foreground font-bold">{it.partNumber}</span>
          <span>{it.epicor.mfgCode ?? '—'}</span>
          <span className={cn(p?.stock === 0 && 'text-destructive font-semibold')}>Dispo {p?.stock ?? '—'}</span>
          {p && p.coreCost !== '0.00' && <span>+ {formatMoney(p.coreCost)} consigne</span>}
        </div>
        {cost && <div className="mt-0.5 text-xs text-amber-800">Coûtant {formatMoney(cost)}</div>}
      </div>
      {/* Colonne de droite : le prix client aligné à droite, la quantité et l'ajout dessous. */}
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {p?.unitPrice ? (
          <div className="text-price text-lg leading-none font-bold whitespace-nowrap tabular-nums">
            {formatMoney(p.unitPrice)}
          </div>
        ) : (
          <div className="text-muted-foreground text-sm font-medium whitespace-nowrap">Appelez-nous</div>
        )}
        <div className="flex items-center gap-1.5">
          <input
            {...qtyInput}
            inputMode="numeric"
            aria-label={`Quantité ${it.partNumber}`}
            className="border-input bg-card focus:border-primary focus:ring-primary/15 h-11 w-12 rounded-md border text-center tabular-nums focus:ring-4 focus:outline-none"
          />
          <button
            {...addButton}
            className="bg-primary text-primary-foreground hover:bg-primary-dark flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-40"
          >
            <ShoppingCart className="size-5" />
          </button>
        </div>
      </div>
    </li>
  );
}

function ResultRow(props: ResultProps) {
  const { it, cost } = props;
  const { qtyInput, addButton } = useResultAdd(props);
  const p = it.price;
  // UnitList : sens encore incertain, montré seulement s'il dépasse le prix appliqué.
  const showList = !!p?.unitPrice && !!p.listPrice && Number(p.listPrice) > Number(p.unitPrice);

  return (
    <tr className="hover:bg-accent/50 border-b transition-colors last:border-0">
      <td className="text-muted-foreground py-2 pr-2 pl-4 font-medium">{it.epicor.mfgCode ?? '—'}</td>
      <td className="px-2 py-2 font-bold whitespace-nowrap tabular-nums">{it.partNumber}</td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-2">
          <ProductThumb src={it.image} />
          <div className="min-w-0">
            <div className="line-clamp-2">{it.title}</div>
            {it.brand && <div className="text-muted-foreground text-xs">{it.brand}</div>}
          </div>
        </div>
      </td>
      <td className="px-2 py-2 text-center">
        <input
          {...qtyInput}
          inputMode="numeric"
          aria-label={`Quantité ${it.partNumber}`}
          className="border-input bg-card focus:border-primary focus:ring-primary/15 h-10 w-14 rounded-md border text-center tabular-nums focus:ring-4 focus:outline-none"
        />
      </td>
      <td className="text-muted-foreground px-2 py-2 text-right tabular-nums">{showList ? formatMoney(p!.listPrice) : ''}</td>
      <td className="px-2 py-2 text-right whitespace-nowrap tabular-nums">
        {p?.unitPrice ? (
          <div className="text-price text-base font-bold">{formatMoney(p.unitPrice)}</div>
        ) : (
          <div className="text-muted-foreground font-medium">Appelez-nous</div>
        )}
        {cost && <div className="text-xs text-amber-800">Coûtant {formatMoney(cost)}</div>}
      </td>
      <td className="text-muted-foreground px-2 py-2 text-right tabular-nums">
        {p && p.coreCost !== '0.00' ? formatMoney(p.coreCost) : ''}
      </td>
      <td className={cn('px-2 py-2 text-right font-medium tabular-nums', p?.stock === 0 && 'text-destructive')}>
        {p?.stock ?? '—'}
      </td>
      <td className="py-2 pr-4 pl-2">
        <button
          {...addButton}
          className="bg-primary text-primary-foreground hover:bg-primary-dark flex size-11 items-center justify-center rounded-lg transition-colors disabled:opacity-40"
        >
          <ShoppingCart className="size-5" />
        </button>
      </td>
    </tr>
  );
}

/** Vignette produit ; icône neutre si l'article n'a pas d'image ou si elle ne charge pas. */
function ProductThumb({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
      {src && !failed ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full bg-white object-contain"
        />
      ) : (
        <Package className="text-muted-foreground size-5" />
      )}
    </div>
  );
}

// --- Ligne de commande -----------------------------------------------------------

const SOURCE_BADGE: Record<QuoteLine['priceSource'], { label: string; className: string } | null> = {
  epicor: null,
  special_quote: { label: 'Prix spécial', className: 'bg-accent text-accent-foreground' },
};

function LineRow({
  line,
  editable,
  syncing,
  cost,
  costUnavailable,
  cart,
  onDone,
}: {
  line: QuoteLine;
  editable: boolean;
  /** Changée à l'écran, pas encore confirmée par le serveur (prix du palier à venir). */
  syncing: boolean;
  cost?: QuoteCosts['lines'][number];
  /** L'API Pièce n'a pas répondu : un coûtant absent est indisponible, pas inconnu. */
  costUnavailable: boolean;
  cart: CartEdits;
  /** Après un changement de ligne : le focus retourne au champ de recherche. */
  onDone: () => void;
}) {
  const [editing, setEditing] = useState(false);
  // Ligne provisoire (ajout pas encore enregistré) : rien à modifier avant son numéro.
  const canEdit = editable && line.id > 0;

  function changeQty(qty: number) {
    cart.changeQty(line.id, qty);
    onDone();
  }
  function applyPrice(change: PriceChange) {
    cart.setPrice(line.id, change);
    setEditing(false);
    onDone();
  }

  const badge = SOURCE_BADGE[line.priceSource];
  const lineTotal = ((Number(line.unitPrice) + Number(line.coreCost)) * line.qty).toFixed(2);

  return (
    <li className="space-y-2 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="leading-snug font-medium">{line.title}</div>
          <div className="text-muted-foreground flex flex-wrap gap-x-3 text-sm">
            <span className="text-foreground font-bold tabular-nums">{line.partNum}</span>
            {line.brand && <span>{line.brand}</span>}
            {line.coreCost !== '0.00' && <span>+ {formatMoney(line.coreCost)} écofrais/unité</span>}
            {badge && <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', badge.className)}>{badge.label}</span>}
          </div>
        </div>
        <div className="text-right tabular-nums">
          <div className="font-bold">{formatMoney(lineTotal)}</div>
          {syncing && <div className="text-muted-foreground animate-pulse text-xs">mise à jour…</div>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="bg-card flex shrink-0 items-center rounded-lg border">
          <button
            {...KEEP_FOCUS}
            onClick={() => changeQty(line.qty - 1)}
            disabled={!canEdit || line.qty <= 1}
            className="hover:bg-muted flex size-10 items-center justify-center rounded-lg transition-colors disabled:opacity-30"
            aria-label="Diminuer"
          >
            <Minus className="size-4" />
          </button>
          <span className="w-10 text-center font-bold tabular-nums">{line.qty}</span>
          <button
            {...KEEP_FOCUS}
            onClick={() => changeQty(line.qty + 1)}
            disabled={!canEdit || line.qty >= 9999}
            className="hover:bg-muted flex size-10 items-center justify-center rounded-lg transition-colors disabled:opacity-30"
            aria-label="Augmenter"
          >
            <Plus className="size-4" />
          </button>
        </div>

        <button
          {...KEEP_FOCUS}
          onClick={() => setEditing((e) => !e)}
          disabled={!canEdit}
          aria-expanded={editing}
          className={cn(
            'flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-lg border px-3 whitespace-nowrap tabular-nums transition-colors disabled:cursor-default',
            line.priceSource === 'special_quote' ? 'border-primary text-primary font-semibold' : 'hover:bg-muted',
          )}
        >
          <Tag className="size-4 shrink-0" />
          {formatMoney(line.unitPrice)} / unité
        </button>
        {editable && (
          <button
            {...KEEP_FOCUS}
            onClick={() => {
              cart.removeLine(line.id);
              onDone();
            }}
            disabled={!canEdit}
            className="text-destructive flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-red-50 disabled:opacity-30"
            aria-label="Retirer l'article"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
      {cost && (
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
            cost.belowCost ? 'bg-red-100 text-red-900' : 'bg-amber-100 text-amber-900',
          )}
        >
          {cost.cost
            ? `Coûtant ${formatMoney(cost.cost)} · marge ${formatPct(cost.marginPct)}`
            : costUnavailable
              ? 'Coûtant indisponible'
              : 'Coûtant inconnu'}
          {cost.belowCost && ' · SOUS LE COÛTANT'}
        </span>
      )}

      {editing && (
        <PriceEditor line={line} onApply={applyPrice} onCancel={() => setEditing(false)} />
      )}
    </li>
  );
}

function PriceEditor({
  line,
  onApply,
  onCancel,
}: {
  line: QuoteLine;
  onApply: (change: PriceChange) => void;
  onCancel: () => void;
}) {
  const [price, setPrice] = useState(line.unitPrice);
  const [error, setError] = useState<string | null>(null);
  // Vérifié ici, avec la règle du serveur : un prix invalide ne quitte pas l'éditeur.
  function apply() {
    const unitPrice = parseMoney(price);
    if (!unitPrice) return setError('Prix invalide');
    onApply({ unitPrice });
  }

  return (
    <div className="bg-muted space-y-3 rounded-lg border p-3">
      <div className="text-muted-foreground flex flex-wrap gap-x-4 text-sm">
        <span>Prix Epicor du client : {line.epicorPrice ? formatMoney(line.epicorPrice) : 'non tarifé'}</span>
        {line.listPrice && <span>Prix liste Epicor : {formatMoney(line.listPrice)}</span>}
      </div>
      <label className="block space-y-1">
        <span className="panel-title block">Prix spécial unitaire</span>
        <input
          value={price}
          onChange={(e) => {
            setPrice(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          inputMode="decimal"
          className="border-input bg-card focus:border-primary focus:ring-primary/15 h-12 w-40 rounded-lg border px-3 text-lg font-bold tabular-nums focus:ring-4 focus:outline-none"
        />
        <span className="text-muted-foreground block text-xs">Pour cette commande seulement.</span>
      </label>
      {error && <p className="text-destructive text-sm">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={apply}
          className="bg-primary text-primary-foreground hover:bg-primary-dark btn-caps h-11 rounded-lg px-4 transition-colors disabled:opacity-50"
        >
          Appliquer
        </button>
        {line.epicorPrice && line.priceSource !== 'epicor' && (
          <button onClick={() => onApply({ resetPrice: true })} className="bg-card hover:bg-accent h-11 rounded-lg border px-4 text-sm font-medium transition-colors">
            Revenir au prix Epicor
          </button>
        )}
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground h-11 px-4 text-sm font-medium">
          Annuler
        </button>
      </div>
    </div>
  );
}
